import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonComponent } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { CheckmarkComponent } from '@app/components/checkmark';
import { HeadingComponent } from '@app/components/heading';
import { InputComponent } from '@app/components/input';
import {
  ToastComponent,
  ToastPosition,
  ToastService,
  ToastType,
} from '@app/components/toast';
import { AdminAuthService } from '@app/services/admin-auth/admin-auth.service';
import { AdminInviteUserService } from '@app/services/admin-invite-user/admin-invite-user.service';
import { AdminUserService } from '@app/services/admin-user/admin-user.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

export enum InviteRouteAction {
  updatePassword = 'update-password',
}

@Component({
  standalone: true,
  selector: 'app-invite-create-password',
  templateUrl: './invite-create-password.component.html',
  styleUrl: './invite-create-password.component.css',
  imports: [
    InputComponent,
    TranslateDirective,
    TranslatePipe,
    ButtonComponent,
    CardComponent,
    HeadingComponent,
    CommonModule,
    RouterModule,
    CheckmarkComponent,
    ToastComponent,
  ],
})
export class InviteCreatePasswordComponent implements OnInit {
  private readonly adminInviteUserService = inject(AdminInviteUserService);
  private readonly route = inject(ActivatedRoute);
  private readonly adminAuthService = inject(AdminAuthService);
  private readonly adminUserService = inject(AdminUserService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  authCode = signal('');
  authId = signal('');
  email = signal('');
  tenantId = signal('');
  currentPassword = signal('');
  confirmPassword = signal('');
  password = signal('');
  isLoading = signal(false);
  userId = signal('');
  passwordCriteria = {
    lengthCheck: computed(
      () =>
        this.password().length >= this.minChar &&
        this.password().length <= this.maxChar
    ),
    uppercase: computed(() => /[A-Z]/.test(this.password())),
    lowercase: computed(() => /[a-z]/.test(this.password())),
    number: computed(() => /[0-9]/.test(this.password())),
    specialChar: computed(() => /[!@#$%^&*+]/.test(this.password())),
    match: computed(
      () =>
        this.password() === this.confirmPassword() &&
        this.password() &&
        this.confirmPassword()
    ),
  };
  isFormValid = computed(() => {
    return Object.values(this.passwordCriteria).every((criteria) => criteria());
  });
  isCurrentUser = false;
  passwordError = false;
  hasNetworkError = true;
  private readonly minChar = 8;
  private readonly maxChar = 64;
  ToastPosition = ToastPosition;

  ngOnInit(): void {
    const action = this.route.snapshot.data.action;
    this.isCurrentUser = action === InviteRouteAction.updatePassword;
    this.authCode.set(this.route.snapshot.paramMap.get('authCode'));
    if (!this.isCurrentUser) {
      this.adminInviteUserService.getInviteInfo(this.authCode()).subscribe({
        next: (res) => {
          this.authId.set(res.authId);
          this.email.set(res.email);
          this.tenantId.set(res.groupId);
        },
        error: (err) => {
          console.error(err);
        },
      });
    } else {
      this.tenantId.set(this.route.snapshot.paramMap.get('tenantId'));
      this.authId.set(this.route.snapshot.paramMap.get('authId'));
      this.userId.set(this.route.snapshot.paramMap.get('userId'));
    }
  }

  onSubmit() {
    this.isLoading.set(true);

    if (this.isFormValid()) {
      if (this.isCurrentUser) {
        this.adminAuthService
          .userUpdatePassword(this.currentPassword(), this.password())
          .pipe(
            switchMap(() => {
              return this.adminUserService.addAdminRole(
                this.tenantId(),
                this.authId(),
                this.userId()
              );
            }),
            tap(() => {
              this.isLoading.set(false);
              this.router.navigateByUrl('/');
            }),
            catchError(({ error }) => {
              this.displayError(error?.error);
              this.isLoading.set(false);
              return EMPTY;
            })
          )
          .subscribe();
      } else {
        this.adminInviteUserService
          .createNewAdminUser(
            this.authId(),
            this.tenantId(),
            this.email(),
            this.password()
          )
          .subscribe({
            next: () => {
              this.isLoading.set(false);
              this.router.navigateByUrl('/login', { replaceUrl: true });
            },
            error: ({ error }) => {
              this.isLoading.set(false);
              this.displayError(error?.error);
            },
          });
      }
    }
  }

  closeToast() {
    this.hasNetworkError = false;
  }

  private displayError(message: string): void {
    if (message) {
      this.toastService.add(message, ToastType.ERROR);
    } else {
      const message = this.translatorService.translate(
        'common.couldNotCreateUser'
      );
      this.toastService.add(message, ToastType.ERROR);
    }
  }
}
